import get from 'lodash/get';
import Constants from '~/config/Constants';
import { checkVideoComprehensionType, isVideoQuestionEnabled } from '~/utils/QuestionUtils';
import { MediaType } from 'shared-domains';
import type { Question } from 'shared-domains';
import $i18n from '~/services/i18n';
import { getCountableQuestions } from './filterQuestionsUtil';
import groupBy from 'lodash/groupBy';
import { isCorporateUser } from '~/utils/QfwUtils';

export type QuestionListTuple = [Question, number];

export type MetaQuestionResponse = Question & {
  comprehensionQuestions: Question[],
};

export const minComprehensionTimer = {
  time: 120000,
  errorMessage: $i18n('Comprehension reading time cannot be less than 2 minutes.'),
};
/**
 * filters out comprehension children questions and return a tuple of question and index at which the question is present in the list
 * @param questions Question[]
 * @returns [Question, index]
 */
export function filterComprehensionChildQuestions(questions: Question[]): QuestionListTuple[] {
  return questions.reduce((list, question, index) => {
    if (question.comprehensionQuestionId) {
      return list;
    }
    return [...list, [question, index]];
  }, [] as QuestionListTuple[]);
}

export function isComprehensionQuestion(question: Question) {
  return question?.type === Constants.QuestionTypes.COMPREHENSION;
}

export function isAnyComprehensionQuestion(question: Question) {
  return isComprehensionQuestion(question) || checkVideoComprehensionType(question);
}

export function isComprehensionChildQuestion(question: Question) {
  return !!question?.comprehensionQuestionId;
}

export function filterComprehensionChildQuestionsWithoutIndex(questions: Question[]): Question[] {
  return questions.filter(question => !question.comprehensionQuestionId);
}

export function getComprehensionChildQuestions(comprehensionQuestion: Question | undefined, questions: Question[]): QuestionListTuple[] {
  const countableQuestions = getCountableQuestions(questions);
  const questionTuple = (comprehensionQuestion?.comprehensionQuestionIds || []).map(
    questionId => ([countableQuestions.find(q => q._id === questionId), countableQuestions.findIndex(q => q._id === questionId)]),
  ) as QuestionListTuple[];

  if (checkVideoComprehensionType(comprehensionQuestion)) {
    return questionTuple.sort((a, b) => a[1] - b[1]);
  }

  return questionTuple;
}

export function getComprehensionParentQuestion(question: Question, questions: Question[]) {
  return questions.find(q => q._id === question.comprehensionQuestionId);
}

export function getNumberOfComprehensionQuestions(question: Question) {
  if (!isAnyComprehensionQuestion(question))
  { return 0; }

  return question.comprehensionQuestionIds?.length || 0;
}

export function filterComprehensionParentQuestions(questions: Question[]): Question[] {
  return questions.filter(q => !isAnyComprehensionQuestion(q));
}

export function getComprehensionParentQuestions(questions: Question[]): Question[] {
  return questions.filter(q => isAnyComprehensionQuestion(q));
}

export function handleComprehensionPublishError(error: any) {
  const errorMessages = {
    'quizVersion.INVALID_COMPREHENSION_QUESTIONS': $i18n('Some {$1} questions are incomplete. Please complete or delete them to proceed', [
      (isVideoQuestionEnabled() || isCorporateUser()) ? '' : 'comprehension',
    ]),
  };
  const errorCode = get(error, 'response.data.error', null) as keyof typeof errorMessages;
  const errorMessage = errorMessages[errorCode];

  return errorMessage;
}

export function getUpdatedComprehensionIdMap(oldComprehensionQuestion: Question, questionResponse: MetaQuestionResponse) {
  const { comprehensionQuestions: children, ...newComprehensionQuestion } = questionResponse;
  const questionUpdateMap = {} as Record<string, Question>;
  questionUpdateMap[oldComprehensionQuestion?._id] = newComprehensionQuestion;
  children.forEach((question) => {
    if (oldComprehensionQuestion._id === questionResponse._id || !question.parent) {
      questionUpdateMap[question._id] = question;
    } else {
      questionUpdateMap[question.parent] = question;
    }
  });
  return questionUpdateMap;
}

export function isComprehensionQueryValid(question: Question) {
  const query = question?.structure?.query;
  const hasPDF = query?.media?.find(mediaObj => mediaObj?.type === MediaType.PDF);
  const hasVideo = query?.media?.find(mediaObj => mediaObj?.type === MediaType.VIDEO);
  if (hasPDF || hasVideo) {
    return true;
  }
  if (!query.text) {
    return false;
  }
  const regex = /<(?!img|audio|video|iframe)[^>]+>/g;
  const cleanedText = query.text.replace(regex, '').trim();
  const hasLatex = query.text.includes('<katex');
  return cleanedText.length > 0 || hasLatex;
}

export function getNonComprehensionQuestions(questions: Question[]) {
  return questions.filter(question => !isAnyComprehensionQuestion(question) && !isComprehensionChildQuestion(question));
}

export function fromComprehensionToVideoQuestion(question: Question[]) {
  return groupBy(question, 'comprehensionTimestamp');
}

export function displayDuration(durationInSec: number, round: boolean = true) {
  const fn = round ? Math.round : Math.floor;
  const roundedSec = fn(durationInSec);
  const secs = roundedSec % 60;
  const mins = Math.floor(roundedSec / 60);

  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}:${`${remainingMins}`.padStart(2, '0')}:${`${secs}`.padStart(2, '0')}`;
  }

  return `${`${mins}`.padStart(2, '0')}:${`${secs}`.padStart(2, '0')}`;
}

// for each question, aggregate how many comprehensions have passed
export function createComphrehensionAggIndex(questions: Question[]): number[] {
  let comprehensionQuestionCount = 0;
  const aggregates = [];
  for (const question of questions) {
    if (isAnyComprehensionQuestion(question)) {
      comprehensionQuestionCount++;
    }
    aggregates.push(comprehensionQuestionCount);
  }
  return aggregates;
}
